import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useAuth } from '../../lib/auth';
import api from '../../lib/api';
import {
  Search,
  Bell,
  Plus,
  Menu,
  User,
  Settings,
  LogOut,
  HelpCircle,
  UserPlus,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';

export const Header = ({ title, onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications (patient requests, appointments)
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      
      try {
        const notificationItems = [];
        
        // Fetch pending patient requests for admin/physician
        if (['admin', 'physician'].includes(user.role)) {
          try {
            const requestsRes = await api.get('/patient-requests', { params: { status: 'pending' } });
            const pendingRequests = requestsRes.data || [];
            pendingRequests.slice(0, 5).forEach(req => {
              notificationItems.push({
                id: req.id,
                type: 'patient_request',
                title: 'New Patient Request',
                message: `${req.first_name} ${req.last_name} - ${req.visit_type}`,
                time: req.submitted_at,
                icon: UserPlus,
                link: '/app/patient-requests',
                unread: true,
              });
            });
          } catch (e) {
            // Silently fail - user may not have access
          }
        }
        
        // Fetch today's appointments
        try {
          const today = new Date().toISOString().split('T')[0];
          const appointmentsRes = await api.get('/appointments', { params: { date: today } });
          const todayAppts = appointmentsRes.data || [];
          if (todayAppts.length > 0) {
            notificationItems.push({
              id: 'today-appts',
              type: 'appointments',
              title: 'Today\'s Appointments',
              message: `You have ${todayAppts.length} appointment${todayAppts.length > 1 ? 's' : ''} today`,
              time: new Date().toISOString(),
              icon: Calendar,
              link: '/app/scheduling',
              unread: false,
            });
          }
        } catch (e) {
          // Silently fail
        }
        
        setNotifications(notificationItems);
        setUnreadCount(notificationItems.filter(n => n.unread).length);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/patients?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleNotificationClick = (notification) => {
    setNotificationsOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    // Format as dd/mmm/yyyy
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <header className="glass-header sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-6" data-testid="header">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <h1 className="text-xl font-semibold tracking-tight hidden sm:block">{title}</h1>
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        {/* Search */}
        <form onSubmit={handleSearch} className="hidden md:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search patients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9 h-9"
              data-testid="header-search"
            />
          </div>
        </form>

        {/* Quick Actions */}
        {hasPermission('patients:create') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="quick-add-btn">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Quick Add</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Create New</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/app/patients/new')} data-testid="add-patient-menu">
                New Patient
              </DropdownMenuItem>
              {hasPermission('forms:create') && (
                <DropdownMenuItem onClick={() => navigate('/app/forms/builder')} data-testid="add-form-menu">
                  New Form Template
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Notifications */}
        <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="p-3 border-b">
              <h3 className="font-semibold">Notifications</h3>
              <p className="text-xs text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} new notification${unreadCount > 1 ? 's' : ''}` : 'No new notifications'}
              </p>
            </div>
            <ScrollArea className="h-[300px]">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => {
                    const Icon = notification.icon || Bell;
                    return (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className="w-full p-3 text-left hover:bg-muted/50 transition-colors flex items-start gap-3"
                      >
                        <div className={`p-2 rounded-full ${notification.unread ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${notification.unread ? 'font-medium' : ''}`}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTime(notification.time)}
                          </p>
                        </div>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
            {notifications.length > 0 && (
              <div className="p-2 border-t">
                <Button 
                  variant="ghost" 
                  className="w-full text-sm" 
                  onClick={() => {
                    setNotificationsOpen(false);
                    navigate('/app/patient-requests');
                  }}
                >
                  View all notifications
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <span className="font-semibold text-xs">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div>
                <p className="font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="settings-menu">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="help-menu">
              <HelpCircle className="mr-2 h-4 w-4" />
              Help & Support
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="logout-menu"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
